// src/utils/appCatalog.ts
import API_BASE_URL from './apiBase';
import { type GameStatus, GAME_STATUS_LABELS } from './gameCatalog';

// Re-export for convenience
export type AppStatus = GameStatus;
export const APP_STATUS_LABELS = GAME_STATUS_LABELS;

// Valid credit billing periods — must match the CHECK constraint
// defined in server/migrations/012_credits_system.sql and widened to
// include 'year' in server/migrations/014_credit_period_year.sql.
export type CreditPeriod = 'day' | 'week' | 'month' | 'year';

export const CREDIT_PERIOD_LABELS: Record<CreditPeriod, string> = {
  day: 'Per Day',
  week: 'Per Week',
  month: 'Per Month',
  year: 'Per Year',
};

export interface AppInfo {
  id: string;
  name: string;
  description: string;
  image?: string;
  appUrl: string;
  status?: AppStatus;
  creditCost: number;
  creditPeriod: CreditPeriod;
}

export const fetchAppsCatalog = async (token?: string | null): Promise<AppInfo[]> => {
  try {
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/apps/catalog`, { headers });
    if (!response.ok) {
      throw new Error('Failed to fetch apps catalog');
    }

    const data = await response.json();
    return data.map((app: {
      id: string; name: string; description: string;
      image_url?: string; app_url: string; status: AppStatus;
      credit_cost?: number; credit_period?: CreditPeriod;
    }) => ({
      id: app.id,
      name: app.name,
      description: app.description,
      image: app.image_url,
      appUrl: app.app_url,
      status: app.status,
      creditCost: Number(app.credit_cost ?? 0),
      creditPeriod: (app.credit_period as CreditPeriod) ?? 'month',
    }));
  } catch (error) {
    console.error('Failed to fetch apps catalog:', error);
    return [];
  }
};

export const fetchAppStatuses = async (token: string): Promise<Record<string, AppStatus>> => {
  try {
    const response = await fetch(`${API_BASE_URL}/apps/statuses`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      throw new Error('Failed to fetch app statuses');
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch app statuses:', error);
    return {};
  }
};

// Shared mapper — backend columns → frontend AppInfo.
const mapAppRow = (row: {
  id: string; name: string; description?: string;
  image_url?: string; app_url: string; status: AppStatus;
  credit_cost?: number; credit_period?: CreditPeriod;
}): AppInfo => ({
  id: row.id,
  name: row.name,
  description: row.description || '',
  image: row.image_url,
  appUrl: row.app_url,
  status: row.status,
  creditCost: Number(row.credit_cost ?? 0),
  creditPeriod: (row.credit_period as CreditPeriod) ?? 'month',
});

export const createApp = async (
  token: string,
  app: {
    name: string;
    appUrl: string;
    status: AppStatus;
    description?: string;
    creditCost?: number;
    creditPeriod?: CreditPeriod;
  }
): Promise<AppInfo | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/apps/catalog`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(app),
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to create app');
    }
    return mapAppRow(await response.json());
  } catch (error) {
    console.error('Failed to create app:', error);
    throw error;
  }
};

export const updateApp = async (
  token: string,
  appId: string,
  data: {
    name: string;
    description: string;
    appUrl: string;
    status: AppStatus;
    creditCost?: number;
    creditPeriod?: CreditPeriod;
  }
): Promise<AppInfo | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/apps/${appId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) return null;
    return mapAppRow(await response.json());
  } catch (error) {
    console.error('Failed to update app:', error);
    return null;
  }
};

/**
 * Dedicated credit config PATCH. Use this when the admin only wants to
 * tweak the price without editing name/URL/status.
 */
export const updateAppCredits = async (
  token: string,
  appId: string,
  data: { creditCost: number; creditPeriod: CreditPeriod },
): Promise<AppInfo | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/apps/${appId}/credits`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) return null;
    return mapAppRow(await response.json());
  } catch (error) {
    console.error('Failed to update app credits:', error);
    return null;
  }
};

export const deleteApp = async (token: string, appId: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/apps/${appId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.ok;
  } catch (error) {
    console.error('Failed to delete app:', error);
    return false;
  }
};

export const updateAppStatus = async (token: string, appId: string, status: AppStatus): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/apps/${appId}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ status }),
    });
    return response.ok;
  } catch (error) {
    console.error('Failed to update app status:', error);
    return false;
  }
};
